import React, { useEffect, useState } from 'react';
import { Tab, Tabs, Table, Badge, Spinner } from 'react-bootstrap';
import axios from 'axios';
import Toast from '../../shared/Toast';
import StandingsTable from '../schedule/standings/StandingsTable';
import GeneralStandingsTable from '../schedule/standings/GeneralStandingsTable';
import PublicBracketsTab from '../game/PublicBracketsTab';
import { dateFormat } from '../../utlils/dateFormat';

const Url = process.env.REACT_APP_MAIN_URL;

const GameCenter = ({ tournamentId, divisionId, teamId, token, refreshTrigger }) => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('schedule');

  useEffect(() => {
    if (tournamentId && divisionId) {
      fetchGames();
    }
  }, [tournamentId, divisionId, teamId, refreshTrigger]);

  // ⭐ UPDATED: use the unified endpoint so bracket/playoff games show up
  // alongside pool play. The backend already limits bracket games to
  // PUBLISHED brackets, so drafts stay hidden from players.
  const fetchGames = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${Url}api/games/unified`, {
        params: { tournamentId, divisionId },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.data.success) {
        let list = response.data.data || [];

        // The unified endpoint has no teamId filter, so narrow it here.
        if (teamId) {
          list = list.filter(
            (g) => g.homeTeamId === teamId || g.awayTeamId === teamId
          );
        }

        setGames(list);
      }
    } catch (error) {
      console.error('Error fetching games:', error);
      Toast.error('Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      SCHEDULED: 'secondary',
      IN_PROGRESS: 'warning',
      COMPLETED: 'success',
      CANCELLED: 'danger',
    };
    return <Badge bg={statusColors[status] || 'secondary'}>{status}</Badge>;
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch (error) {
      return timeString;
    }
  };

  // ⭐ UPDATED: unified games carry gameDateTime, not gameDate. Bracket games
  // that haven't been scheduled yet have no date at all — they group under
  // "Unscheduled" rather than vanishing.
  const groupGamesByDate = () => {
    const grouped = {};
    games.forEach((game) => {
      const date = game.gameDateTime ? game.gameDateTime.split('T')[0] : 'UNSCHEDULED';
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(game);
    });
    return grouped;
  };

  // ⭐ Pool play shows its pool name; bracket games show the round.
  const renderRound = (game) => {
    if (game.gameType === 'PLAYOFF') {
      return (
        <Badge bg="danger">
          {game.roundName || `Round ${game.roundNumber}`}
        </Badge>
      );
    }
    return game.poolName ? game.poolName.replace('_', ' ') : '-';
  };

  const renderSchedule = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading schedule...</p>
        </div>
      );
    }

    if (games.length === 0) {
      return (
        <div className="text-center py-5">
          <h5 className="text-muted">No games scheduled</h5>
          <p className="text-muted">
            {teamId
              ? 'Your team has no games scheduled yet'
              : 'Games will appear here once scheduled'}
          </p>
        </div>
      );
    }

    const groupedGames = groupGamesByDate();

    // Unscheduled bucket sorts last
    const dateKeys = Object.keys(groupedGames).sort((a, b) => {
      if (a === 'UNSCHEDULED') return 1;
      if (b === 'UNSCHEDULED') return -1;
      return a.localeCompare(b);
    });

    return (
      <div className="schedule-container">
        {dateKeys.map((date) => (
          <div key={date} className="mb-4">
            <h5 className="fw-bold mb-3">
              {date === 'UNSCHEDULED' ? 'Not yet scheduled' : dateFormat(date)}
            </h5>
            <div style={{ overflowX: 'auto' }}>
              <Table striped bordered hover responsive>
                <thead className="bg-dark text-white">
                  <tr>
                    <th>Time</th>
                    <th>Field</th>
                    <th>Home Team</th>
                    <th className="text-center">Score</th>
                    <th>Away Team</th>
                    <th className="text-center">Round</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedGames[date].map((game) => (
                    <tr
                      key={`${game.gameType}-${game.gameId}`}
                      className={
                        teamId &&
                        (game.homeTeamId === teamId || game.awayTeamId === teamId)
                          ? 'table-primary'
                          : ''
                      }
                    >
                      <td>{formatTime(game.gameTime) || '-'}</td>
                      <td>{game.fieldNumber || '-'}</td>
                      <td>
                        {game.homeTeamName}
                        {teamId && game.homeTeamId === teamId && (
                          <Badge bg="primary" className="ms-2">
                            You
                          </Badge>
                        )}
                        {game.gameStatus === 'COMPLETED' &&
                          game.homeTeamScore > game.awayTeamScore && (
                            <span
                              className="text-success ms-2 fw-bold"
                              style={{ fontSize: '1.2rem' }}
                              title="Winner"
                            >
                              ✓
                            </span>
                          )}
                      </td>
                      <td className="text-center">
                        {game.homeTeamScore !== null && game.awayTeamScore !== null
                          ? `${game.homeTeamScore} - ${game.awayTeamScore}`
                          : '-'}
                      </td>
                      <td>
                        {game.awayTeamName}
                        {teamId && game.awayTeamId === teamId && (
                          <Badge bg="primary" className="ms-2">
                            You
                          </Badge>
                        )}
                        {game.gameStatus === 'COMPLETED' &&
                          game.awayTeamScore > game.homeTeamScore && (
                            <span
                              className="text-success ms-2 fw-bold"
                              style={{ fontSize: '1.2rem' }}
                              title="Winner"
                            >
                              ✓
                            </span>
                          )}
                      </td>
                      <td className="text-center">{renderRound(game)}</td>
                      <td className="text-center">{getStatusBadge(game.gameStatus)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="game-center-container">
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >
        <Tab eventKey="schedule" title="Schedule">
          {renderSchedule()}
        </Tab>
        <Tab eventKey="standings" title="Pool Play Standings">
          <StandingsTable
            divisionId={divisionId}
            teamId={teamId}
            token={token}
            refreshTrigger={refreshTrigger}
          />
        </Tab>
        {/* ⭐ NEW: pool play + brackets combined */}
        <Tab eventKey="general" title="Tournament Standings">
          <GeneralStandingsTable
            divisionId={divisionId}
            teamId={teamId}
            token={token}
            refreshTrigger={refreshTrigger}
          />
        </Tab>
        {/* ⭐ NEW: live bracket, read-only. Published brackets only. */}
        <Tab eventKey="brackets" title="Brackets">
          <PublicBracketsTab
            divisionId={divisionId}
            refreshTrigger={refreshTrigger}
          />
        </Tab>
      </Tabs>
    </div>
  );
};

export default GameCenter;